import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import {
  AdvancedMapState,
  DashboardLayerConfig,
  GlobalDateFilter,
  IdValue,
  MapStyleType,
} from 'types/types';

function getYesterdayDateRange() {
  const today = new Date();
  const yesterday = new Date(today);
  const beforeYesterday = new Date(today);

  // Subtract one and two days from today
  yesterday.setDate(today.getDate() - 1);
  beforeYesterday.setDate(today.getDate() - 2);

  // Format the dates in the required format (ISO 8601 without milliseconds for UTC)
  const formatDate = (date) => {
    return date.toISOString().replace(/\.\d{3}Z$/, '.000Z');
  };

  // Create start and end dates
  const startDate = new Date(yesterday.setHours(0, 0, 0, 0));
  const endDate = new Date(yesterday.setHours(23, 59, 59, 999));

  // Return the object with id and name formatted as required
  return [
    {
      id: `${formatDate(startDate)}|${formatDate(endDate)}`,
      name: `${yesterday.getDate().toString().padStart(2, '0')}/${(
        yesterday.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}/${yesterday.getFullYear()} To ${yesterday
        .getDate()
        .toString()
        .padStart(2, '0')}/${(yesterday.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${yesterday.getFullYear()}`,
    },
    {
      id: 'daily',
      name: 'Daily',
    },
  ];
}

const initialState: AdvancedMapState = {
  zoom: 12,
  center: [48.853533880372815, -12.608184814453127],
  selectedBands: [],
  granularityDates: {
    filterId: 'global_date_filter',
    values: getYesterdayDateRange(),
  },
  globalLayers: [],
  globalLayersLoaded: false,
  mapStyles: {
    options: ['Bing Grey'],
    selected: 'Bing Grey',
  },
  bandsList: [],
  clickedCell: null,
  highlightedCriterias: [],
};

export const setGlobalLayersAsync = createAsyncThunk(
    'advancedMap/setGlobalLayersAsync',
    async (globalLayers: DashboardLayerConfig[], { dispatch }) => {
        await dispatch(setGlobalLayers(globalLayers)); // Ensure this action is awaited
        return globalLayers; // Returns a promise
    }
);

const advancedMapSlice = createSlice({
  name: 'advancedMapState',
  initialState,
  reducers: {
    setMapPosition: (
      state,
      action: PayloadAction<{
        zoom: number;
        center: number[];
      }>
    ) => {
      state.zoom = action.payload.zoom;
      state.center = action.payload.center;
    },
    setSelectedBands: (state, action: PayloadAction<string[]>) => {
      state.selectedBands = action.payload;
    },
    setGranularityDates: (state, action: PayloadAction<GlobalDateFilter>) => {
      state.granularityDates = action.payload;
    },
    setGlobalLayers: (state, action: PayloadAction<DashboardLayerConfig[]>) => {
      state.globalLayers = action.payload;
    },
    updateLayerByLayerId: (state, action: PayloadAction<{ LayerId: string, diff: any }>) => {
      state.globalLayers = state.globalLayers.map((layer) => layer.LayerId === action.payload.LayerId ? {...layer, ...action.payload.diff} : layer);
    },
    setGlobalLayersLoaded: (state, action: PayloadAction<boolean>) => {
      state.globalLayersLoaded = action.payload;
    },
    setSelectedMapStyle: (state, action: PayloadAction<MapStyleType>) => {
      state.mapStyles.selected = action.payload;
    },
    setBandsList: (state, action: PayloadAction<string[]>) => {
      state.bandsList = action.payload;
    },
    setClickedCell: (state, action: PayloadAction<any>) => {
      state.clickedCell = action.payload;
    },
    resetMapState: () => initialState,
    setHighlightedCriterias: (state, action: PayloadAction<IdValue[]>) => {
      state.highlightedCriterias = action.payload;
    },
  },
});

export const {
  setMapPosition,
  setGlobalLayers,
  updateLayerByLayerId,
  setGlobalLayersLoaded,
  setSelectedBands,
  setGranularityDates,
  setSelectedMapStyle,
  setBandsList,
  setClickedCell,
  resetMapState,
  setHighlightedCriterias,
} = advancedMapSlice.actions;

export const zoom = (state) => {
  return state.advancedMap.zoom;
};

export const center = (state) => {
  return state.advancedMap.center;
};

export default advancedMapSlice.reducer;
